import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parseApiError } from '@saas-kit/lib';
import { Button, Drawer, ErrorBanner, Field, InputField, Select, Textarea } from '@saas-kit/ui';

import type { PaymentMethod, RecordPaymentPayload } from './api';

const METHODS: PaymentMethod[] = [
  'bank_transfer',
  'cash',
  'check',
  'external_wire',
  'other',
];

interface Props {
  open: boolean;
  invoiceNumber: string;
  currency: string;
  outstandingAmount: string;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: RecordPaymentPayload) => Promise<void>;
}

interface FormState {
  amount: string;
  payment_method: PaymentMethod;
  reference: string;
  received_at: string;
  notes: string;
}

function defaultReceivedAt(): string {
  const now = new Date();
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(
    now.getHours(),
  )}:${pad(now.getMinutes())}`;
}

export function RecordPaymentDialog({
  open,
  invoiceNumber,
  currency,
  outstandingAmount,
  isPending,
  onOpenChange,
  onSubmit,
}: Props): JSX.Element {
  const { t } = useTranslation('adminBilling');
  const [form, setForm] = useState<FormState>(() => ({
    amount: outstandingAmount,
    payment_method: 'bank_transfer',
    reference: '',
    received_at: defaultReceivedAt(),
    notes: '',
  }));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({
        amount: outstandingAmount,
        payment_method: 'bank_transfer',
        reference: '',
        received_at: defaultReceivedAt(),
        notes: '',
      });
      setError(null);
    }
  }, [open, outstandingAmount]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(event: React.FormEvent): void {
    event.preventDefault();
    setError(null);
    const payload: RecordPaymentPayload = {
      amount: form.amount.trim(),
      payment_method: form.payment_method,
      reference: form.reference.trim() || null,
      received_at: new Date(form.received_at).toISOString(),
      notes: form.notes.trim() || null,
    };
    void (async () => {
      try {
        await onSubmit(payload);
        onOpenChange(false);
      } catch (err) {
        setError(parseApiError(err).message);
      }
    })();
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={t('payment.title')}
      description={t('payment.description', {
        invoice: invoiceNumber,
        amount: outstandingAmount,
        currency,
      })}
      testId="payment-record-drawer"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('payment.cancel')}
          </Button>
          <Button
            type="submit"
            form="payment-record-form"
            disabled={isPending}
          >
            {isPending ? t('payment.saving') : t('payment.submit')}
          </Button>
        </div>
      }
    >
      <form
        id="payment-record-form"
        className="space-y-4"
        onSubmit={handleSubmit}
      >
        {error ? <ErrorBanner>{error}</ErrorBanner> : null}
        <InputField
          label={t('payment.amount', { currency })}
          name="amount"
          required
          inputMode="decimal"
          placeholder="0.00"
          value={form.amount}
          onChange={(e) => update('amount', e.target.value)}
        />
        <Field label={t('payment.method')} htmlFor="payment-method">
          <Select
            id="payment-method"
            value={form.payment_method}
            onChange={(e) => update('payment_method', e.target.value as PaymentMethod)}
          >
            {METHODS.map((method) => (
              <option key={method} value={method}>
                {t(`payment.methods.${method}`)}
              </option>
            ))}
          </Select>
        </Field>
        <InputField
          label={t('payment.received_at')}
          name="received_at"
          type="datetime-local"
          required
          value={form.received_at}
          onChange={(e) => update('received_at', e.target.value)}
        />
        <InputField
          label={t('payment.reference')}
          name="reference"
          placeholder={t('payment.reference_placeholder')}
          value={form.reference}
          onChange={(e) => update('reference', e.target.value)}
        />
        <Field label={t('payment.notes')} htmlFor="payment-notes">
          <Textarea
            id="payment-notes"
            rows={3}
            value={form.notes}
            onChange={(e) => update('notes', e.target.value)}
          />
        </Field>
      </form>
    </Drawer>
  );
}
